import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Building2, Plus, CheckCircle2, Trash2, Loader2, Sparkles,
  Mail, Copy, Info, X, Lock, PencilLine,
} from "lucide-react";

export default function AccountCompanies() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const isCommercialista = !!user?.is_commercialista;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setCompanies(data || []);
    } catch (e) {
      toast.error("Errore caricamento aziende");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Always refresh the user profile from server so `is_commercialista`
    // reflects the latest admin toggle (bypassing stale localStorage cache).
    refresh?.();
    load();
  }, []);

  const activate = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/companies/${id}/activate`);
      toast.success("Azienda attivata");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa azienda? L'operazione è irreversibile")) return;
    setBusyId(id);
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Azienda eliminata");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusyId(null);
    }
  };

  const copy = (v) => {
    if (!v) return;
    navigator.clipboard.writeText(v);
    toast.success("Copiato negli appunti");
  };

  const onCreated = async () => {
    setShowCreate(false);
    await load();
    toast.success("Azienda creata con successo");
  };

  return (
    <div className="space-y-6" data-testid="account-companies">
      <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-lg">Le tue aziende</h2>
            <p className="text-sm text-zinc-500 mt-0.5">
              {isCommercialista
                ? "Gestisci pi\u00f9 profili fiscali sotto lo stesso account. I crediti sono globali su tutto l'account"
                : "L'azienda associata al tuo profilo. Per modificare i dati fiscali contatta il supporto"}
            </p>
          </div>
          {isCommercialista && (
            <button
              onClick={() => setShowCreate(true)}
              data-testid="new-company-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1D4ED8] text-white text-sm font-semibold hover:bg-[#1E3A8A] shadow-sm shadow-blue-600/20 transition-colors"
            >
              <Plus className="w-4 h-4" /> Nuova azienda
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-12 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
        ) : companies.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#E8E4D9] p-8 text-center text-sm text-zinc-500">
            Nessuna azienda configurata
          </div>
        ) : (
          <ul className="space-y-3" data-testid="companies-list">
            {companies.map((c) => (
              <li
                key={c.id}
                data-testid={`company-row-${c.id}`}
                className={`rounded-lg p-4 sm:p-5 transition-colors border ${
                  c.is_active ? "border-blue-500 bg-blue-50/50" : "border-[#E8E4D9] bg-white hover:border-zinc-400"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-lg grid place-items-center shrink-0 ${
                    c.is_active ? "bg-[#1D4ED8] text-white" : "bg-[#F5F2EA] text-zinc-700"
                  }`}>
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-zinc-900 truncate">{c.denominazione || "\u2014"}</span>
                      {c.is_active && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold uppercase tracking-wider">
                          <Sparkles className="w-3 h-3" /> Attiva
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono truncate mt-0.5">
                      {c.partita_iva ? `P.IVA ${c.partita_iva}` : "P.IVA mancante"}
                      {c.comune ? ` \u00b7 ${c.comune}` : ""}
                      {c.provincia ? ` (${c.provincia})` : ""}
                    </div>
                    {c.inbound_email && (
                      <div className="mt-2 flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-[#737373] shrink-0" />
                        <code className="text-[11px] font-mono text-[#0A0A0A] truncate flex-1">{c.inbound_email}</code>
                        <button
                          onClick={() => copy(c.inbound_email)}
                          data-testid={`copy-inbound-${c.id}`}
                          title="Copia indirizzo email inbound"
                          className="p-1.5 rounded-md hover:bg-white border border-[#E8E4D9]"
                        >
                          <Copy className="w-3 h-3 text-[#4A4A4A]" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {isCommercialista && (
                      <button
                        onClick={async () => {
                          if (!c.is_active) {
                            setBusyId(c.id);
                            try {
                              await api.post(`/companies/${c.id}/activate`);
                              toast.success("Cliente attivato");
                              navigate("/app/company");
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Errore");
                              setBusyId(null);
                              return;
                            }
                          } else {
                            navigate("/app/company");
                          }
                        }}
                        disabled={busyId === c.id}
                        data-testid={`edit-company-${c.id}`}
                        title="Modifica dati anagrafici"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border border-[#E8E4D9] bg-white text-[#0A0A0A] hover:border-[#0A0A0A] disabled:opacity-60 transition-colors"
                      >
                        {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PencilLine className="w-3 h-3" />}
                        Modifica
                      </button>
                    )}
                    {!c.is_active && (
                      <button
                        onClick={() => activate(c.id)}
                        disabled={busyId === c.id}
                        data-testid={`activate-company-${c.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-60 transition-colors"
                      >
                        {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Attiva
                      </button>
                    )}
                    {isCommercialista && (
                      <button
                        onClick={() => remove(c.id)}
                        disabled={busyId === c.id || companies.length <= 1}
                        data-testid={`delete-company-${c.id}`}
                        title={companies.length <= 1 ? "Non puoi eliminare l'unica azienda" : "Elimina"}
                        className="p-2 rounded-md hover:bg-red-50 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {!isCommercialista && (
        <section className="rounded-xl border border-blue-200 bg-blue-50 p-6" data-testid="commercialista-upsell">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-semibold text-blue-900">Gestisci pi&ugrave; aziende clienti</h3>
              <p className="text-sm text-blue-800 mt-1 max-w-2xl">
                Solo gli account <strong>Commercialista</strong> possono creare pi&ugrave; profili fiscali sotto lo stesso account. Ogni cliente avr&agrave; la sua email dedicata per inbound e il suo sezionale, con crediti condivisi
              </p>
              <a
                href="mailto:supporto@dasabo.com?subject=Sblocco%20profilo%20Commercialista"
                data-testid="commercialista-upsell-cta"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900 text-white text-sm font-semibold hover:bg-blue-800 transition-colors"
              >
                Sblocca profilo Commercialista
              </a>
            </div>
          </div>
        </section>
      )}

      {!isCommercialista && (
        <p className="text-xs text-zinc-500 inline-flex items-center gap-2">
          <Info className="w-3.5 h-3.5" />
          I dati fiscali non sono modificabili dall&apos;interfaccia utente. Per modifiche contatta il supporto
        </p>
      )}
      {isCommercialista && (
        <p className="text-xs text-[#4A4A4A] inline-flex items-center gap-2">
          <Info className="w-3.5 h-3.5" />
          Clicca <span className="font-semibold">Modifica</span> per cambiare i dati anagrafici del cliente. L&apos;azione attiva il cliente e apre il profilo modificabile
        </p>
      )}

      {showCreate && (
        <CreateCompanyModal onClose={() => setShowCreate(false)} onCreated={onCreated} />
      )}
    </div>
  );
}

function CreateCompanyModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    denominazione: "", partita_iva: "", codice_fiscale: "",
    indirizzo: "", cap: "", comune: "", provincia: "",
    email_pec: "", codice_destinatario: "0000000",
  });
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.denominazione.trim() || !/^[0-9]{11}$/.test(form.partita_iva)) {
      toast.error("Denominazione e P.IVA (11 cifre) sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      await api.post("/companies", {
        ...form,
        provincia: form.provincia.toUpperCase(),
        nazione: "IT",
        codice_destinatario: form.codice_destinatario.trim().toUpperCase().padEnd(7, "0").slice(0, 7),
      });
      onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      toast.error(msg || "Errore creazione azienda");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="create-company-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E8E4D9] flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold">Nuovo cliente</div>
            <h3 className="font-display font-extrabold text-2xl mt-1">Aggiungi una nuova azienda</h3>
            <p className="text-sm text-zinc-500 mt-1">Compila i dati fiscali. Dopo il salvataggio saranno bloccati (contatta il supporto per modifiche)</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-zinc-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Denominazione" required>
              <Input value={form.denominazione} onChange={upd("denominazione")} placeholder="Ragione sociale" testid="create-denominazione" />
            </Field>
            <Field label="Partita IVA" required>
              <Input value={form.partita_iva} onChange={upd("partita_iva")} placeholder="11 cifre" mono maxLength={11} testid="create-piva" />
            </Field>
            <Field label="Codice Fiscale">
              <Input value={form.codice_fiscale} onChange={upd("codice_fiscale")} mono maxLength={16} placeholder="11 o 16 caratteri" testid="create-cf" />
            </Field>
            <Field label="PEC">
              <Input type="email" value={form.email_pec} onChange={upd("email_pec")} placeholder="pec@esempio.it" testid="create-pec" />
            </Field>
            <Field label="Indirizzo" span={2}>
              <Input value={form.indirizzo} onChange={upd("indirizzo")} placeholder="Via e numero civico" testid="create-address" />
            </Field>
            <Field label="CAP"><Input value={form.cap} onChange={upd("cap")} mono maxLength={5} testid="create-cap" /></Field>
            <Field label="Comune"><Input value={form.comune} onChange={upd("comune")} testid="create-city" /></Field>
            <Field label="Provincia"><Input value={form.provincia} onChange={upd("provincia")} placeholder="Es. MI" maxLength={2} mono testid="create-prov" /></Field>
            <Field label="Codice destinatario SDI">
              <Input value={form.codice_destinatario} onChange={upd("codice_destinatario")} mono maxLength={7} testid="create-sdi" />
            </Field>
          </div>
          <div className="flex items-center gap-2 pt-4 border-t border-[#E8E4D9]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-[#E8E4D9] text-sm font-medium hover:bg-zinc-50"
            >Annulla</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="create-company-submit"
              className="ml-auto inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[#1D4ED8] text-white text-sm font-semibold hover:bg-[#1E3A8A] disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Crea azienda
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children, span = 1 }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="flex items-center gap-1 text-xs font-medium text-zinc-700 mb-1">
        <span>{label}</span>
        {required && <span className="text-red-600">*</span>}
      </label>
      {children}
    </div>
  );
}

function Input({ mono, testid, className = "", ...props }) {
  return (
    <input
      data-testid={testid}
      className={`w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 outline-none transition-colors ${mono ? "font-mono" : ""} ${className}`}
      {...props}
    />
  );
}
